/**
 * A tiny RGBA raster for pixel art. No anti-aliasing: every shape snaps to whole pixels,
 * so sprites stay crisp when the world is scaled up 4×. Runs the same in the browser and in Node.
 */

export type RGB = readonly [number, number, number];
export type RGBA = readonly [number, number, number, number];

export function hex(h: string): RGB {
  const s = h.replace("#", "");
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}

export function shade(c: RGB, k: number): RGB {
  return [clamp255(c[0] * k), clamp255(c[1] * k), clamp255(c[2] * k)];
}

export function mix(a: RGB, b: RGB, t: number): RGB {
  return [clamp255(a[0] + (b[0] - a[0]) * t), clamp255(a[1] + (b[1] - a[1]) * t), clamp255(a[2] + (b[2] - a[2]) * t)];
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

export const OUTLINE: RGB = [40, 28, 34];
export const INK: RGB = [34, 24, 36];

export class Bitmap {
  readonly data: Uint8ClampedArray;

  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.data = new Uint8ClampedArray(w * h * 4);
  }

  get(x: number, y: number): RGBA {
    const i = (y * this.w + x) * 4;
    return [this.data[i], this.data[i + 1], this.data[i + 2], this.data[i + 3]];
  }

  alpha(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return 0;
    return this.data[(y * this.w + x) * 4 + 3];
  }

  set(x: number, y: number, c: RGB, a = 255): void {
    x |= 0;
    y |= 0;
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = (y * this.w + x) * 4;
    if (a >= 255) {
      this.data[i] = c[0];
      this.data[i + 1] = c[1];
      this.data[i + 2] = c[2];
      this.data[i + 3] = 255;
    } else if (a > 0) {
      // straight alpha "over"
      const da = this.data[i + 3] / 255;
      const sa = a / 255;
      const oa = sa + da * (1 - sa);
      for (let k = 0; k < 3; k++) this.data[i + k] = (c[k] * sa + this.data[i + k] * da * (1 - sa)) / oa;
      this.data[i + 3] = oa * 255;
    }
  }

  clear(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    this.data[(y * this.w + x) * 4 + 3] = 0;
  }

  rect(x: number, y: number, w: number, h: number, c: RGB, a = 255): void {
    const x0 = Math.max(0, Math.round(x));
    const y0 = Math.max(0, Math.round(y));
    const x1 = Math.min(this.w, Math.round(x + w));
    const y1 = Math.min(this.h, Math.round(y + h));
    for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) this.set(xx, yy, c, a);
  }

  /** Inclusive box from (x0,y0) to (x1,y1), like PIL's rectangle. */
  box(x0: number, y0: number, x1: number, y1: number, c: RGB, outline?: RGB): void {
    this.rect(x0, y0, x1 - x0 + 1, y1 - y0 + 1, c);
    if (outline) this.strokeBox(x0, y0, x1, y1, outline);
  }

  strokeBox(x0: number, y0: number, x1: number, y1: number, c: RGB): void {
    this.line(x0, y0, x1, y0, c);
    this.line(x0, y1, x1, y1, c);
    this.line(x0, y0, x0, y1, c);
    this.line(x1, y0, x1, y1, c);
  }

  line(x0: number, y0: number, x1: number, y1: number, c: RGB, a = 255): void {
    x0 = Math.round(x0);
    y0 = Math.round(y0);
    x1 = Math.round(x1);
    y1 = Math.round(y1);
    const dx = Math.abs(x1 - x0);
    const dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      this.set(x0, y0, c, a);
      if (x0 === x1 && y0 === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x0 += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y0 += sy;
      }
    }
  }

  /** Scanline polygon fill (even-odd), pixel centres. */
  poly(pts: readonly (readonly [number, number])[], c: RGB, a = 255): void {
    if (pts.length < 3) return;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [, y] of pts) {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
    const y0 = Math.max(0, Math.floor(minY));
    const y1 = Math.min(this.h - 1, Math.ceil(maxY));
    const xs: number[] = [];
    for (let y = y0; y <= y1; y++) {
      const cy = y + 0.5;
      xs.length = 0;
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i];
        const [bx, by] = pts[(i + 1) % pts.length];
        if ((ay <= cy && by > cy) || (by <= cy && ay > cy)) xs.push(ax + ((cy - ay) * (bx - ax)) / (by - ay));
      }
      xs.sort((p, q) => p - q);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const xa = Math.max(0, Math.ceil(xs[k] - 0.5));
        const xb = Math.min(this.w - 1, Math.floor(xs[k + 1] - 0.5));
        for (let x = xa; x <= xb; x++) this.set(x, y, c, a);
      }
    }
  }

  ellipse(x0: number, y0: number, x1: number, y1: number, c: RGB, a = 255): void {
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const rx = (x1 - x0) / 2 + 0.5;
    const ry = (y1 - y0) / 2 + 0.5;
    for (let y = Math.floor(y0); y <= Math.ceil(y1); y++) {
      for (let x = Math.floor(x0); x <= Math.ceil(x1); x++) {
        const dx = (x + 0.5 - (cx + 0.5)) / rx;
        const dy = (y + 0.5 - (cy + 0.5)) / ry;
        if (dx * dx + dy * dy <= 1) this.set(x, y, c, a);
      }
    }
  }

  /** Copy another bitmap on top (alpha over). */
  blit(src: Bitmap, dx: number, dy: number, flipX = false): void {
    dx = Math.round(dx);
    dy = Math.round(dy);
    for (let y = 0; y < src.h; y++) {
      for (let x = 0; x < src.w; x++) {
        const sx = flipX ? src.w - 1 - x : x;
        const i = (y * src.w + sx) * 4;
        const a = src.data[i + 3];
        if (a) this.set(dx + x, dy + y, [src.data[i], src.data[i + 1], src.data[i + 2]], a);
      }
    }
  }

  /** A 1-pixel outline around everything opaque (like PIL's MaxFilter trick in the mocks). */
  outlined(c: RGB = OUTLINE): Bitmap {
    const out = new Bitmap(this.w + 2, this.h + 2);
    for (let y = 0; y < out.h; y++) {
      for (let x = 0; x < out.w; x++) {
        if (this.alpha(x - 1, y - 1)) continue;
        let near = false;
        for (let oy = -1; oy <= 1 && !near; oy++) for (let ox = -1; ox <= 1 && !near; ox++) if (this.alpha(x - 1 + ox, y - 1 + oy) > 128) near = true;
        if (near) out.set(x, y, c);
      }
    }
    out.blit(this, 1, 1);
    return out;
  }

  crop(x: number, y: number, w: number, h: number): Bitmap {
    const out = new Bitmap(w, h);
    for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) {
      const sx = x + xx;
      const sy = y + yy;
      if (sx < 0 || sy < 0 || sx >= this.w || sy >= this.h) continue;
      const i = (sy * this.w + sx) * 4;
      const o = (yy * w + xx) * 4;
      out.data[o] = this.data[i];
      out.data[o + 1] = this.data[i + 1];
      out.data[o + 2] = this.data[i + 2];
      out.data[o + 3] = this.data[i + 3];
    }
    return out;
  }

  /** Nearest-neighbour upscale. */
  scaled(k: number): Bitmap {
    const out = new Bitmap(this.w * k, this.h * k);
    for (let y = 0; y < out.h; y++) for (let x = 0; x < out.w; x++) {
      const i = (((y / k) | 0) * this.w + ((x / k) | 0)) * 4;
      const o = (y * out.w + x) * 4;
      out.data[o] = this.data[i];
      out.data[o + 1] = this.data[i + 1];
      out.data[o + 2] = this.data[i + 2];
      out.data[o + 3] = this.data[i + 3];
    }
    return out;
  }

  /** Bounding box of opaque pixels, or null if empty. */
  bounds(): { x: number; y: number; w: number; h: number } | null {
    let x0 = this.w;
    let y0 = this.h;
    let x1 = -1;
    let y1 = -1;
    for (let y = 0; y < this.h; y++) for (let x = 0; x < this.w; x++) if (this.data[(y * this.w + x) * 4 + 3]) {
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
    return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
  }

  opaqueCount(): number {
    let n = 0;
    for (let i = 3; i < this.data.length; i += 4) if (this.data[i]) n++;
    return n;
  }

  /** Stable fingerprint of the pixels, for tests ("is this sprite distinct?"). */
  hash(): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < this.data.length; i++) {
      h ^= this.data[i];
      h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
  }
}

/** Deterministic tiny PRNG for art (independent of the game's streams). */
export function artRng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
